import { SessionSocketEvent } from '@elearning-platform/api-types/enums/api/authorization';
import { SessionUpdatePayloadInterface } from '@elearning-platform/api-types/interfaces/api/authorization';
import { UseNotifications, useNotifications, UseState } from '@elearning-platform/ui';
import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { io, Socket } from 'socket.io-client';

import { extendRefreshToken, refreshAccessToken } from '../../api/requests';
import { PersistGateContext, SocketContext } from '../../contexts';
import { useAuth } from '../../hooks';
import { PersistGateContextData, RefreshAccessTokenResponse, UseAuth } from '../../interfaces';

import { SocketProviderProps } from './socketProvider.types';

export const SocketProvider: FC<SocketProviderProps> = (props: SocketProviderProps): ReactElement => {
  const { t }: TransProps<never> = useTranslation();
  const { children }: SocketProviderProps = props;
  const [socket, setSocket]: UseState<Socket | undefined> = useState<Socket | undefined>();
  const [isAuthorizedToSocket, setIsAuthorizedToSocket]: UseState<boolean> = useState<boolean>(false);
  const { signOutCleanup, signOut, userToken, setTokenAfterSignIn, isLoggedIn }: UseAuth = useAuth();
  const { showPopup, hidePopup, addToast }: UseNotifications = useNotifications();
  const accessTokenRefreshTimeout: MutableRefObject<NodeJS.Timeout | null> = useRef<NodeJS.Timeout | null>(null);
  const { websocketDomain }: PersistGateContextData = useContext(PersistGateContext);
  const accessTokenRefreshBufferSeconds: number = 15;
  const isExpiringPopupVisible: MutableRefObject<boolean> = useRef<boolean>(false);

  const trackSession: (secondsToExpiry: number) => void = useCallback((secondsToExpiry: number): void => {
    if (accessTokenRefreshTimeout.current) {
      clearTimeout(accessTokenRefreshTimeout.current);
    }
    accessTokenRefreshTimeout.current = setTimeout(
      (): void => {
        refreshAccessToken()
          .then((response: RefreshAccessTokenResponse): void => {
            trackSession(response.data.secondsToExpire);
            setTokenAfterSignIn(response.data.token);
          })
          .catch((): undefined => undefined);
      },
      (secondsToExpiry - accessTokenRefreshBufferSeconds) * 1_000
    );
  }, [accessTokenRefreshTimeout, setTokenAfterSignIn]);

  const refreshAccessTokenAction: () => Promise<void> = useCallback(
    (): Promise<void> => new Promise(
      (resolve: () => void, reject: () => void): void => {
        refreshAccessToken()
          .then((response: RefreshAccessTokenResponse): void => {
            resolve();
            trackSession(response.data.secondsToExpire);
            setTokenAfterSignIn(response.data.token);
          })
          .catch(reject);
      }
    ),
    [setTokenAfterSignIn, trackSession]
  );

  const handleAutoLogout: () => void = useCallback((): void => {
    if (accessTokenRefreshTimeout.current) {
      clearTimeout(accessTokenRefreshTimeout.current);
    }
    signOutCleanup(true);
    addToast({ text: t('competence.app.socketProvider.autoLogout') });
    setIsAuthorizedToSocket(false);
  }, [addToast, signOutCleanup, t]);

  useEffect(
    (): (() => void) => {
      if (!userToken || isAuthorizedToSocket || !websocketDomain) {
        return (): undefined => undefined;
      }

      const socketIo: Socket = io(websocketDomain, { path: '/socket.io', auth: { token: userToken } });
      socketIo.on('connect', (): void => setSocket(socketIo));
      socketIo.on('disconnect', (): void => setSocket(undefined));
      socketIo.on(SessionSocketEvent.Expired, handleAutoLogout);
      socketIo.on(SessionSocketEvent.Logout, handleAutoLogout);
      socketIo.on(SessionSocketEvent.Expiring, (): void => {
        isExpiringPopupVisible.current = true;
        showPopup({
          title: t('competence.app.socketProvider.sessionExpiringPopup.title'),
          description: t('competence.app.socketProvider.sessionExpiringPopup.description'),
          acceptButtonData: {
            label: t('competence.app.socketProvider.sessionExpiringPopup.acceptAction'),
            onClick: (): void => {
              void extendRefreshToken()
                .catch((): void => {
                  refreshAccessTokenAction()
                    .then((): Promise<void> => extendRefreshToken())
                    .catch((): undefined => undefined);
                });
              hidePopup();
              isExpiringPopupVisible.current = false;
            },
          },
          cancelButtonData: {
            label: t('competence.app.socketProvider.sessionExpiringPopup.cancelAction'),
            onClick: (): void => {
              signOut();
              hidePopup();
              isExpiringPopupVisible.current = false;
            },
          },
        });
      });
      socketIo.on(SessionSocketEvent.Update, (data: SessionUpdatePayloadInterface): void => {
        if (data.authToken?.secondsToExpiry) {
          trackSession(data.authToken.secondsToExpiry);
        }
        if (isExpiringPopupVisible.current) {
          hidePopup();
          isExpiringPopupVisible.current = false;
        }
      });

      return (): void => {
        socketIo.off('connect');
        socketIo.off('disconnect');
        socketIo.off(SessionSocketEvent.Logout);
        socketIo.offAny();
        socketIo.close();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthorizedToSocket, showPopup, hidePopup, t, trackSession, handleAutoLogout, signOut, websocketDomain, isLoggedIn]
  );

  return (
    <SocketContext.Provider value={socket}>
      {children}
    </SocketContext.Provider>
  );
};
